import { Injectable, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { v4 as uuidv4 } from 'uuid';
import { SpecificationType, SpecLine } from '../../features/asset-management/models/SpecLine';
import { Specification } from '../../features/asset-management/models/Specification';
import { ProductsState } from '../state/ProductsState';
import { resolveDefinitionField } from '../../features/asset-management/models/request/UpdateSpecificationLine';
import { Store } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import { TranslationService } from '../translations/services/translation.service';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  sharedData = signal<string>('bottom right');
  private alertService = inject(AlertService);
  private translationService = inject(TranslationService);
  private fb = inject(FormBuilder);

  // Map of alert keys to alert configuration. Content can be a translation key.
  private alertMappings: Record<string, { type: AlertType; title?: string; content: string; translate?: boolean }> = {
    WORK_ORDER_UPDATE_SUCCESS: { type: AlertType.success, content: 'workOrderUpdateSuccessText', translate: true },
    NO_FIELD_TO_UPDATE: { type: AlertType.success, content: 'workOrderUpdateSuccessText', translate: true },
    WORK_ORDER_UPDATE_FAIL: { type: AlertType.critical, content: 'workOrderUpdateSuccessTextFailedText', translate: true },
    GENERIC_ERROR: { type: AlertType.critical, content: 'genericErrorText', translate: true },
    WORK_ORDER_NUMBER_EXISTS: { type: AlertType.strong, content: 'workOrderNumberExistsText', translate: true },
    INVALID_RECEIVED_DATE: { type: AlertType.critical, content: 'invalidReceivedDate', translate: true },
    INVALID_DUE_DATE: { type: AlertType.critical, content: 'invalidDueDate', translate: true },
    DUE_DATE_BEFORE_RECEIVED_DATE_OR_TURN_TIME: { type: AlertType.critical, content: 'dueDateValidationText', translate: true },
    WORK_ORDER_NOT_EDITABLE: { type: AlertType.strong, content: 'workOrderNotEditableText', translate: true }
  };

  // Returns alert configuration (level and message) based on key.
  getAlertMapping(key: string): { type: AlertType; title: string; content: string } {
    const cfg = this.alertMappings[key] ?? { type: AlertType.critical, content: 'An unexpected error occurred.' };
    const title = cfg.title ? (cfg.translate ? this.translationService.translate(cfg.title) : cfg.title) : '';
    const content = cfg.translate ? this.translationService.translate(cfg.content) : cfg.content;
    return { type: cfg.type, title, content };
  }

  // Convenience method to directly show alert by key.
  showMappedAlert(key: string) {
    const { type, title, content } = this.getAlertMapping(key);
    this.showAlert(type, title, content);
  }

  showAlert(getType: AlertType, getTitle: string, getContent: string) {
    this.alertService.showAlert({
      type: getType,
      title: getTitle,
      content: getContent
    } as Alert);
  }

  updateSharedData(value: string) {
    this.sharedData.set(value);
  }

  getSpecificationFormGroup() {
    return this.fb.group({
      specCode: ['', Validators.required],
      specDescription: ['', Validators.required],
      specType: ['LOCAL', Validators.required],
      lines: this.fb.array([])
    });
  }

  getDefinitionFormGroup() {
    return this.fb.group({
      applicationDefinition: ['', Validators.required],
      axles: ['', [Validators.required]],
      treads: ['', [Validators.required]],
      makes: ['', [Validators.required]],
      series: ['', [Validators.required]],
      sizes: ['', [Validators.required]]
    });
  }

  getActionDetailsFormGroup() {
    return this.fb.group({
      limit: ['', Validators.required],
      action: ['', Validators.required],
      disposition: [''],
      disposalCode: [''],
      application: [''],
      axle: [''],
      treads: ['']
    });
  }

  getLimitFormGroup(isFirstRepairLimit: boolean) {
    return this.fb.group({
      totalRepairs: [0, Validators.required],
      totalNail: [0, [Validators.required, this.repairLimitValidator('totalRepairs')]],
      totalRnailCrown: [0, [Validators.required, this.repairLimitValidator('totalRepairs')]],
      totalRnailShoulder: [0, [Validators.required, this.repairLimitValidator('totalRepairs')]],
      rNailShoulderSize: [''],
      totalSection: [0, [Validators.required, this.repairLimitValidator('totalRepairs')]],
      totalCrownSection: [0, [Validators.required, this.repairLimitValidator('totalSection')]],
      maxCrownSize: [''],
      totalShoulderSection: [0, [Validators.required, this.repairLimitValidator('totalSection')]],
      maxShoulderSize: [''],
      totalSidewallSection: [0, [Validators.required, this.repairLimitValidator('totalSection')]],
      maxSidewallSize: [''],
      spot: [0, Validators.required],
      bead: [0, Validators.required],
      liner: [0, Validators.required],
      brakeSkid: [0, Validators.required],
      action: ['', isFirstRepairLimit ? Validators.required : []],
      disposition: [''],
      disposalCode: [''],
      application: [''],
      axle: [''],
      treads: ['']
    });
  }

  public repairLimitValidator(targetControl: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const currentValue = control.value;
      const controlToCompare = control.parent?.get(targetControl);

      if (controlToCompare && controlToCompare.value < currentValue) {
        return { repairLimitValidator: true };
      }
      return null;
    };
  }

  public duplicateCombinationValidator(currentSpecLine: SpecLine, specification: Specification, specificationLine: any, store: Store) {
    let combinationExist = false;
    const specType = currentSpecLine.type;
    const specLine: any = {
      id: currentSpecLine.id,
      makes: resolveDefinitionField(
        specificationLine.definition.makes.map((x) => this.translationService.translateToEnglish(x)),
        store.selectSnapshot(ProductsState.products).makes.length
      ),
      series: resolveDefinitionField(
        specificationLine.definition.series.map((x) => this.translationService.translateToEnglish(x)),
        store.selectSnapshot(ProductsState.products).series.length
      ),
      sizes: resolveDefinitionField(
        specificationLine.definition.sizes.map((x) => this.translationService.translateToEnglish(x)),
        store.selectSnapshot(ProductsState.products).sizes.length
      ),
      applicationDefinition: specificationLine.definition.applicationDefinition,
      axles: resolveDefinitionField(
        specificationLine.definition.axles.map((x) => this.translationService.translateToEnglish(x)),
        store.selectSnapshot(ProductsState.products).axles.length
      ),
      treads:
        SpecificationType.REPAIR_RETREAD === specType
          ? resolveDefinitionField(
              specificationLine.definition.treads.map((x) => this.translationService.translateToEnglish(x)),
              store.selectSnapshot(ProductsState.products).treads.length
            )
          : null
    };

    specification.specificationLines.some((specItem) => {
      const treadMatch =
        SpecificationType.REPAIR_RETREAD === specType && specItem.treads !== null
          ? specItem.treads.some((tread) => specLine.treads.includes(tread))
          : false;
      const sizeMatch = specItem.sizes.some((size) => specLine.sizes.includes(size));
      const seriesMatch = specItem.series.some((series) => specLine.series.includes(series));
      const makeMatch = specItem.makes.some((make) => specLine.makes.includes(make));
      const axleMatch = specItem.axles.some((axle) => specLine.axles.includes(axle));
      const applicationMatch = isEqual(specItem.applicationDefinition, specLine.applicationDefinition);
      const existingLine = specLine.id !== undefined ? isEqual(specItem.id, specLine.id) : false;
      const specTypeMatch = isEqual(specItem.type, specType);

      if (
        axleMatch &&
        sizeMatch &&
        seriesMatch &&
        makeMatch &&
        applicationMatch &&
        (treadMatch || SpecificationType.REPAIR_RETREAD !== specType) &&
        !existingLine &&
        specTypeMatch
      ) {
        combinationExist = true;
        return true;
      }
    });
    return combinationExist;
  }

  generateUuid(): string {
    return uuidv4();
  }
}
